import { useMutation, useQuery } from "@tanstack/react-query";
import useAxiosInstance from "./axiosInstance.utils";
import { errorFormat } from "./handleError.utils";
import { onSuccessMessage, queryClient } from "./reactQuery.utils";
import { arrayMap, arrayTextPrint } from "./array.utils";
import { quotationFormatter } from "./quotation.utils";

/**
 * :::::::MUTATIONS:::::::::::
 */

export function useCreateCustomerMutation(keyData) {
  const { postApi } = useAxiosInstance();
  async function createCustomerApi(data) {
    try {
      const api = `/customer`;
      const apiResponse = await postApi(api, data);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["createCustomerApi", ...keyData],
    mutationFn: (data) => createCustomerApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("customers");
      onSuccessMessage(data, 201);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, createCustomer: mutateAsync };
}

export function useUpdateCustomerMutation(keyData) {
  const { putApi } = useAxiosInstance();
  async function updateCustomerApi(data) {
    try {
      const api = `/customer`;
      const apiResponse = await putApi(api, data);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }
  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["updateCustomerApi", ...keyData],
    mutationFn: (data) => updateCustomerApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("customers");
      onSuccessMessage(data, 200);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, updateCustomer: mutateAsync };
}

export function useDeleteCustomerMutation(keyData) {
  const { deleteApi } = useAxiosInstance();
  async function deleteCustomerApi(id) {
    try {
      const api = `/customer/${id}`;
      const apiResponse = await deleteApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["deleteCustomerApi", ...keyData],
    mutationFn: (data) => deleteCustomerApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("customers");
      onSuccessMessage(data, 200);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, deleteCustomer: mutateAsync };
}

/**
 * :::::::QUERIES:::::::::::
 */
export function useGetAllCustomerQuery({ query, options = {}, key = "" }) {
  const { getApi } = useAxiosInstance();
  async function getAllCustomerApi(query) {
    try {
      const api = `/customers${query ?? ""}`;
      const apiResponse = await getApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const {
    data,
    isPending: loading,
    ...rest
  } = useQuery({
    queryKey: [`getAllCustomer-${query ?? ""}${key ? "-" + key : ""}`],
    queryFn: () => getAllCustomerApi(query),
    ...options
  });
  const newData = data?.data?.data ?? {};
  const parents = data?.data?.parents ?? [];
  return { data: newData, parents, loading, ...rest };
}

export function useGetCustomerQuery({ query, options = {}, key = "" }) {
  const { getApi } = useAxiosInstance();
  async function getCustomerApi(query) {
    try {
      const api = `/customer${query ?? ""}`;
      const apiResponse = await getApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { data, isPending: loading } = useQuery({
    queryKey: [`getCustomer-${query ?? ""}${key ? "-" + key : ""}`],
    queryFn: () => getCustomerApi(query),
    ...options
  });
  const newData = data?.data?.data ?? {};
  return { data: newData, loading };
}

// functions

const GENDER = [
  {
    value: 1,
    label: "Male"
  },
  {
    value: 2,
    label: "Female"
  }
];

export function getAllGender() {
  return GENDER;
}

export function getGenderByValue(value) {
  return GENDER.find((item) => item.value === value)?.label;
}

// customer profile address fromatter
export function customerProfileAddressFormatter(addresses) {
  return (
    arrayMap(addresses, (item) => {
      return {
        type: item?.type?.name ?? "",
        address:
          item?.is_dhaka_city === 1
            ? [
                {
                  label: "City Name",
                  value: ""
                },
                {
                  label: "Thana",
                  value: item?.dhaka_city?.dhaka_city_thana?.name ?? ""
                },
                {
                  label: "Area",
                  value: item?.dhaka_city?.name ?? ""
                },
                {
                  label: "Post Code",
                  value: item?.dhaka_city?.dhaka_city_thana?.post_code ?? ""
                },
                {
                  label: "Street Address",
                  value: item?.street ?? ""
                }
              ]
            : item?.is_dhaka_city === 2
              ? [
                  {
                    label: "Division",
                    value: item?.division?.name ?? "",
                    id: item?.division?.id
                  },
                  {
                    label: "District",
                    value: item?.district?.name ?? "",
                    id: item?.district?.id
                  },
                  {
                    label: "Upazila",
                    value: item?.upazila?.name ?? "",
                    id: item?.upazila?.id
                  },
                  {
                    label: "Union",
                    value: item?.union?.name ?? "",
                    id: item?.union?.id
                  },
                  {
                    label: "Village",
                    value: item?.village?.name ?? "",
                    id: item?.village?.id
                  },
                  {
                    label: "Post Office",
                    value: item?.post_office?.name ?? "",
                    id: item?.post_office?.id
                  },
                  {
                    label: "Post Code",
                    value: item?.post_office?.post_code ?? ""
                  },
                  {
                    label: "Street Address",
                    value: item?.street ?? ""
                  }
                ]
              : [],
        editItem: item,
        isDhakaCity: item?.is_dhaka_city
      };
    }) || []
  );
}

// customer profile formatter
export function customerProfileFormatter(data) {
  return {
    profile: {
      image: data?.image ?? "",
      gender_name: getGenderByValue(data?.gender),
      gender: data?.gender,
      name: data?.name ?? "",
      role: data?.role?.name ?? "",
      id: data?.id ?? "",
      mobile: data?.mobile ?? "",
      email: data?.email ?? "",
      customer_id: data?.customer_id ?? ""
    },
    address: customerProfileAddressFormatter(data?.addresses),
    attachments:
      arrayMap(data?.attachments, (item) => {
        if (item?.type_id === 1) {
          return quotationFormatter(item);
        }

        return null;
      }) || [],
    old: data
  };
}

// get short information for attachment
export function customerFormatForAttachment(data) {
  const customer = customerProfileFormatter(data);
  const profile = customer?.profile;
  const address = customer?.address?.length ? customer.address[0] : null;

  function getField(fieldName) {
    return address?.address?.find((adItem) => adItem.label === fieldName)?.value || "";
  }

  const fullAddress =
    address?.isDhakaCity === 1
      ? [
          getField("Street Address"),
          getField("Area"),
          getField("Thana"),
          getField("City Name"),
          getField("Post Code")
        ]
      : address?.isDhakaCity === 2
        ? [
            getField("Street Address"),
            getField("Village"),
            getField("Post Office"),
            getField("Union"),
            getField("Upazila"),
            getField("District"),
            getField("Division"),
            getField("Post Code")
          ]
        : [];

  return { profile, address: arrayTextPrint(fullAddress) };
}
